import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { quoteContentAPI } from '../../services/api';
import { useNotificationHook } from '../../hooks/useNotificationHook';
import { useCurrency } from '../../contexts/CurrencyContext';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Globe,
  Code,
  Smartphone,
  ShoppingCart,
  Palette,
  CheckCircle,
  Clock,
  DollarSign,
  CheckSquare,
  Square,
  Zap,
  Loader2
} from 'lucide-react';

const QuoteContentManagement = () => {
  const { notify } = useNotificationHook();
  const { formatPrice } = useCurrency();
  const [activeTab, setActiveTab] = useState('project-types');
  const [projectTypes, setProjectTypes] = useState([]);
  const [features, setFeatures] = useState([]);
  const [timelines, setTimelines] = useState([]);
  const [complexityLevels, setComplexityLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Bulk actions state
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Available icons for project types
  const availableIcons = [
    { name: 'Globe', component: Globe },
    { name: 'Code', component: Code },
    { name: 'Smartphone', component: Smartphone },
    { name: 'ShoppingCart', component: ShoppingCart },
    { name: 'Palette', component: Palette },
    { name: 'CheckCircle', component: CheckCircle },
    { name: 'Clock', component: Clock },
    { name: 'DollarSign', component: DollarSign }
  ];

  const getIconComponent = (iconName) => {
    const icon = availableIcons.find(i => i.name === iconName);
    return icon ? icon.component : Globe;
  };

  // Fetch data based on active tab
  const fetchData = async () => {
    try {
      setLoading(true);
      switch (activeTab) {
        case 'project-types':
          const projectTypesResponse = await quoteContentAPI.getProjectTypesAdmin();
          if (projectTypesResponse?.data?.success) {
            setProjectTypes(projectTypesResponse.data.data);
          }
          break;
        case 'features':
          const featuresResponse = await quoteContentAPI.getAllFeatures();
          if (featuresResponse?.data?.success) {
            setFeatures(featuresResponse.data.data);
          }
          break;
        case 'timelines':
          const timelinesResponse = await quoteContentAPI.getTimelinesAdmin();
          if (timelinesResponse?.data?.success) {
            setTimelines(timelinesResponse.data.data);
          }
          break;
        case 'complexity-levels':
          const complexityResponse = await quoteContentAPI.getComplexityLevelsAdmin();
          if (complexityResponse?.data?.success) {
            setComplexityLevels(complexityResponse.data.data);
          }
          break;
      }
          } catch (error) {
        console.error('Error fetching data:', error);
        notify.error('Failed to fetch data', 'Loading Error');
      } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const clearMessage = () => {
    setMessage({ type: '', text: '' });
  };

  // Bulk action functions
  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAll = () => {
    const currentItems = getCurrentItems();
    if (selectAll) {
      setSelectedItems([]);
      setSelectAll(false);
    } else {
      setSelectedItems(currentItems.map(item => item.id));
      setSelectAll(true);
    }
  };

  const getCurrentItems = () => {
    switch (activeTab) {
      case 'project-types':
        return projectTypes;
      case 'features':
        return features;
      case 'timelines':
        return timelines;
      case 'complexity-levels':
        return complexityLevels;
      default:
        return [];
    }
  };

  const handleBulkStatusUpdate = async (is_active) => {
    if (selectedItems.length === 0) return;
    
    try {
      setBulkActionLoading(true);
      const response = await quoteContentAPI.bulkUpdateStatus(activeTab, selectedItems, is_active);
      
              if (response.data.success) {
          notify.success(`Successfully updated ${selectedItems.length} items to ${is_active ? 'active' : 'inactive'} status`, 'Status Updated');
          setSelectedItems([]);
          setSelectAll(false);
          await fetchData();
        } else {
          notify.error('Failed to update items: ' + (response.data.message || 'Unknown error'), 'Update Error');
        }
      } catch (error) {
        console.error('Error updating items:', error);
        notify.error('Error updating items: ' + (error.response?.data?.message || error.message || 'Unknown error'), 'Network Error');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedItems.length} items? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setBulkActionLoading(true);
      const response = await quoteContentAPI.bulkDelete(activeTab, selectedItems);
      
              if (response.data.success) {
          notify.success(`Successfully deleted ${selectedItems.length} items`, 'Items Deleted');
          setSelectedItems([]);
          setSelectAll(false);
          await fetchData();
        } else {
          notify.error('Failed to delete items: ' + (response.data.message || 'Unknown error'), 'Delete Error');
        }
      } catch (error) {
        console.error('Error deleting items:', error);
        notify.error('Error deleting items: ' + (error.response?.data?.message || error.message || 'Unknown error'), 'Network Error');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Control bulk actions bar visibility
  useEffect(() => {
    setShowBulkActions(selectedItems.length > 0);
  }, [selectedItems]);

  // Form state
  const [formData, setFormData] = useState({});

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle add new item
  const handleAdd = async () => {
    try {
      console.log('🔍 Adding item with data:', formData);
      let response;
      switch (activeTab) {
        case 'project-types':
          response = await quoteContentAPI.createProjectType(formData);
          break;
        case 'features':
          response = await quoteContentAPI.createFeature(formData);
          break;
        case 'timelines':
          response = await quoteContentAPI.createTimeline(formData);
          break;
        case 'complexity-levels':
          response = await quoteContentAPI.createComplexityLevel(formData);
          break;
      }

      console.log('✅ Add response:', response);

              if (response?.data?.success) {
          notify.success('Item added successfully', 'Success');
          setShowAddForm(false);
          setShowModal(false);
          setFormData({});
          fetchData();
        } else {
          notify.error(response?.data?.message || 'Failed to add item', 'Add Error');
        }
      } catch (error) {
        console.error('❌ Error adding item:', error);
        notify.error('Failed to add item', 'Network Error');
    }
  };

  // Handle update item
  const handleUpdate = async () => {
    try {
      console.log('🔍 Updating item with data:', formData);
      let response;
      switch (activeTab) {
        case 'project-types':
          response = await quoteContentAPI.updateProjectType(editingItem.id, formData);
          break;
        case 'features':
          response = await quoteContentAPI.updateFeature(editingItem.id, formData);
          break;
        case 'timelines':
          response = await quoteContentAPI.updateTimeline(editingItem.id, formData);
          break;
        case 'complexity-levels':
          response = await quoteContentAPI.updateComplexityLevel(editingItem.id, formData);
          break;
      }

      console.log('✅ Update response:', response);

              if (response?.data?.success) {
          notify.success('Item updated successfully', 'Success');
          setEditingItem(null);
          setShowModal(false);
          setFormData({});
          fetchData();
        } else {
          notify.error(response?.data?.message || 'Failed to update item', 'Update Error');
        }
      } catch (error) {
        console.error('❌ Error updating item:', error);
        notify.error('Failed to update item', 'Network Error');
    }
  };

  // Handle delete item
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      let response;
      switch (activeTab) {
        case 'project-types':
          response = await quoteContentAPI.deleteProjectType(id);
          break;
        case 'features':
          response = await quoteContentAPI.deleteFeature(id);
          break;
        case 'timelines':
          response = await quoteContentAPI.deleteTimeline(id);
          break;
        case 'complexity-levels':
          response = await quoteContentAPI.deleteComplexityLevel(id);
          break;
      }

              if (response?.data?.success) {
          notify.success('Item deleted successfully', 'Deleted');
          fetchData();
        } else {
          notify.error('Failed to delete item', 'Delete Error');
        }
      } catch (error) {
        console.error('Error deleting item:', error);
        notify.error('Failed to delete item', 'Network Error');
    }
  };

  // Start editing an item
  const startEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      icon: item.icon || 'Globe',
      base_price: item.base_price || item.basePrice || 0,
      price: item.price || 0,
      is_included: item.is_included || false,
      duration: item.duration || '',
      multiplier: item.multiplier || 1.0,
      sort_order: item.sort_order || 0,
      is_active: item.is_active !== undefined ? item.is_active : true,
      project_type_id: item.project_type_id || item.projectTypeId || ''
    });
    setShowModal(true);
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingItem(null);
    setFormData({});
    setShowModal(false);
  };

  // Cancel add form
  const cancelAdd = () => {
    setShowAddForm(false);
    setFormData({});
    setShowModal(false);
  };

  // Open add modal
  const openAddModal = () => {
    setEditingItem(null);
    setFormData({});
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              Quote Content Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage project types, features, timelines, and complexity levels for the quote calculator
            </p>
          </div>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-lg mb-6 ${
          message.type === 'success' 
            ? 'bg-green-100 text-green-700 border border-green-200' 
            : 'bg-red-100 text-red-700 border border-red-200'
        }`}>
          <div className="flex justify-between items-center">
            <span>{message.text}</span>
            <button onClick={clearMessage} className="text-gray-500 hover:text-gray-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'project-types', name: 'Project Types' },
            { id: 'features', name: 'Features' },
            { id: 'timelines', name: 'Timelines' },
            { id: 'complexity-levels', name: 'Complexity Levels' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Bulk Actions */}
      {showBulkActions && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 rounded-lg mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} selected
              </span>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleBulkStatusUpdate(true)}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                  Activate
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate(false)}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <X className="h-3 w-3 mr-1" />}
                  Deactivate
                </button>
                
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content based on active tab */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="p-4 sm:p-6">
          {activeTab === 'project-types' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Project Types ({projectTypes.length})
                  </h3>
                  <button
                    onClick={handleSelectAll}
                    className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  >
                    {selectAll ? (
                      <CheckSquare className="h-4 w-4 text-blue-600" />
                    ) : (
                      <Square className="h-4 w-4 text-gray-400" />
                    )}
                    Select All
                  </button>
                </div>
                <button
                  onClick={openAddModal}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Project Type
                </button>
              </div>
              
              <div className="space-y-4 mt-6">
                {projectTypes.map((projectType) => (
                  <div key={projectType.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleSelectItem(projectType.id)}
                          className="flex items-center"
                        >
                          {selectedItems.includes(projectType.id) ? (
                            <CheckSquare className="h-4 w-4 text-blue-600" />
                          ) : (
                            <Square className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                        {(() => {
                          const IconComponent = getIconComponent(projectType.icon);
                          return <IconComponent className="w-6 h-6 text-primary-600" />;
                        })()}
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            {projectType.name}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {projectType.description}
                          </p>
                          <p className="text-sm text-primary-600 dark:text-primary-400">
                            Starting from {formatPrice(projectType.base_price)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => startEdit(projectType)}
                          className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg"
                          title="Edit project type"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(projectType.id)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded-lg"
                          title="Delete project type"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'features' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Features ({features.length})
                </h3>
                <button
                  onClick={openAddModal}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Feature
                </button>
              </div>
              
              <div className="space-y-4 mt-6">
                {features.map((feature) => (
                  <div key={feature.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {feature.name}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {feature.description}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Project Type: {feature.project_type_name}
                        </p>
                        <p className="text-sm text-primary-600 dark:text-primary-400">
                          {feature.is_included ? 'Included' : `+${formatPrice(feature.price)}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => startEdit(feature)}
                          className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg"
                          title="Edit feature"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(feature.id)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded-lg"
                          title="Delete feature"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'timelines' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Timelines ({timelines.length})
                </h3>
                <button
                  onClick={openAddModal}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Timeline
                </button>
              </div>
              
              <div className="space-y-4 mt-6">
                {timelines.map((timeline) => (
                  <div key={timeline.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {timeline.name}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {timeline.description}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Duration: {timeline.duration}
                        </p>
                        <p className="text-sm text-primary-600 dark:text-primary-400">
                          Multiplier: {timeline.multiplier}x
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => startEdit(timeline)}
                          className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg"
                          title="Edit timeline"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(timeline.id)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded-lg"
                          title="Delete timeline"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'complexity-levels' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Complexity Levels ({complexityLevels.length})
                </h3>
                <button
                  onClick={openAddModal}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Complexity Level
                </button>
              </div>
              
              <div className="space-y-4 mt-6">
                {complexityLevels.map((level) => (
                  <div key={level.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {level.name}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {level.description}
                        </p>
                        <p className="text-sm text-primary-600 dark:text-primary-400">
                          Multiplier: {level.multiplier}x
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => startEdit(level)}
                          className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg"
                          title="Edit complexity level"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(level.id)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded-lg"
                          title="Delete complexity level"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingItem ? 'Edit' : 'Add'} {activeTab === 'project-types' ? 'Project Type' : 
                 activeTab === 'features' ? 'Feature' : 
                 activeTab === 'timelines' ? 'Timeline' : 'Complexity Level'}
              </h2>
              <button
                onClick={editingItem ? cancelEdit : cancelAdd}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Enter name"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  rows="3"
                  placeholder="Enter description"
                />
              </div>

              {/* Project Type specific fields */}
              {activeTab === 'project-types' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Icon
                    </label>
                    <select
                      value={formData.icon || 'Globe'}
                      onChange={(e) => handleInputChange('icon', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    >
                      {availableIcons.map(icon => (
                        <option key={icon.name} value={icon.name}>
                          {icon.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Base Price (₹)
                    </label>
                    <input
                      type="number"
                      value={formData.base_price || ''}
                      onChange={(e) => handleInputChange('base_price', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Enter base price"
                    />
                  </div>
                </>
              )}

              {/* Feature specific fields */}
              {activeTab === 'features' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Project Type
                    </label>
                    <select
                      value={formData.project_type_id || ''}
                      onChange={(e) => handleInputChange('project_type_id', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Select Project Type</option>
                      {projectTypes.map(type => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Price (₹)
                    </label>
                    <input
                      type="number"
                      value={formData.price || ''}
                      onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Enter price"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_included || false}
                      onChange={(e) => handleInputChange('is_included', e.target.checked)}
                      className="mr-2"
                    />
                    <label className="text-sm text-gray-700 dark:text-gray-300">
                      Included (Free)
                    </label>
                  </div>
                </>
              )}

              {/* Timeline specific fields */}
              {activeTab === 'timelines' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Duration
                    </label>
                    <input
                      type="text"
                      value={formData.duration || ''}
                      onChange={(e) => handleInputChange('duration', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., 2-4 weeks"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Multiplier
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.multiplier || ''}
                      onChange={(e) => handleInputChange('multiplier', parseFloat(e.target.value) || 1.0)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Enter multiplier"
                    />
                  </div>
                </>
              )}

              {/* Complexity Level specific fields */}
              {activeTab === 'complexity-levels' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Multiplier
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.multiplier || ''}
                    onChange={(e) => handleInputChange('multiplier', parseFloat(e.target.value) || 1.0)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Enter multiplier"
                  />
                </div>
              )}

              {/* Common fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sort Order
                </label>
                <input
                  type="number"
                  value={formData.sort_order || ''}
                  onChange={(e) => handleInputChange('sort_order', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Enter sort order"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active !== undefined ? formData.is_active : true}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  className="mr-2"
                />
                <label className="text-sm text-gray-700 dark:text-gray-300">
                  Active
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={editingItem ? cancelEdit : cancelAdd}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={editingItem ? handleUpdate : handleAdd}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
              >
                {editingItem ? (
                  <>
                    <Save className="w-4 h-4" />
                    Update
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Add
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default QuoteContentManagement; 